/**
 * Authentication and role detection for DEX UI.
 * Validates API keys and detects user role via /me endpoint.
 */

import { api, setApiKey, ApiError } from './client.js';

/**
 * API key format regex.
 * Format: dx_<role>_<hex>
 * Role prefixes in key: super, admin, writer, reader
 * Maps to roles: super_admin, admin, writer, reader
 */
const API_KEY_REGEX = /^dx_(super|admin|writer|reader)_[0-9a-f]{16,}$/;

/**
 * Map key prefix to internal role name.
 */
const ROLE_PREFIX_MAP = {
  'super': 'super_admin',
  'admin': 'admin',
  'writer': 'writer',
  'reader': 'reader'
};

/**
 * Validate API key format (client-side quick check).
 * @param {string} key - API key to validate
 * @returns {Object} {valid: boolean, hint?: string, roleHint?: string}
 */
export function validateKeyFormat(key) {
  if (!key || typeof key !== 'string') {
    return { valid: false, hint: 'API key is required' };
  }

  key = key.trim();

  if (!key.startsWith('dx_')) {
    return { valid: false, hint: 'Key must start with "dx_"' };
  }

  if (!API_KEY_REGEX.test(key)) {
    return { valid: false, hint: 'Invalid key format' };
  }

  // Extract role hint from key prefix and map to internal role name
  const roleMatch = key.match(/^dx_(super|admin|writer|reader)_/);
  const rolePrefix = roleMatch ? roleMatch[1] : null;
  const roleHint = rolePrefix ? ROLE_PREFIX_MAP[rolePrefix] : null;

  return { valid: true, roleHint };
}

/**
 * Mask an API key for display.
 * Shows prefix and last 4 characters.
 * @param {string} key - API key to mask
 * @returns {string} Masked key
 */
export function maskApiKey(key) {
  if (!key || key.length <= 12) return key || '';

  // Try to extract prefix (dx_role_)
  const prefixMatch = key.match(/^(dx_\w+_)/);
  if (prefixMatch) {
    const prefix = prefixMatch[1];
    const suffix = key.slice(-4);
    const masked = '*'.repeat(Math.max(4, key.length - prefix.length - 4));
    return `${prefix}${masked}${suffix}`;
  }

  // Fallback: show first 4 and last 4
  return `${key.slice(0, 4)}${'*'.repeat(key.length - 8)}${key.slice(-4)}`;
}

/**
 * Detect user role via the /me endpoint.
 * Single API call replaces cascading endpoint probes (no more 403s).
 * @param {string} apiKey - API key to test
 * @returns {Promise<Object>} {role, partyId}
 * @throws {ApiError} If key is invalid or role is unsupported
 */
export async function detectRole(apiKey) {
  setApiKey(apiKey);

  try {
    const identity = await api.get('/me');
    const role = identity.role;
    const partyId = identity.party_id;

    // Validate role is one the UI supports
    const SUPPORTED_ROLES = ['super_admin', 'admin', 'writer', 'reader'];
    if (!SUPPORTED_ROLES.includes(role)) {
      throw new ApiError(400, 'This role is not supported by the UI');
    }

    return { role, partyId };
  } catch (error) {
    setApiKey(null);
    throw error;
  }
}

/**
 * Authenticate with an API key.
 * Validates format, detects role, and returns auth data.
 * @param {string} apiKey - API key to authenticate with
 * @returns {Promise<Object>} {apiKey, role, partyId}
 * @throws {Error} If validation or role detection fails
 */
export async function authenticateKey(apiKey) {
  // Quick format validation
  const formatCheck = validateKeyFormat(apiKey);
  if (!formatCheck.valid) {
    throw new Error(formatCheck.hint);
  }

  // Detect role via API
  const { role, partyId } = await detectRole(apiKey);

  return {
    apiKey,
    role,
    partyId
  };
}
